// Removed OpenAI import - using Lambda function instead
import { Container, Box, Typography, Fade, Grow, CircularProgress, Chip } from '@mui/material';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import Avatar from '@mui/material/Avatar';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { withAuth } from '../../../components/HOC/withAuth';
import useResponsive from '../../../hooks/useResponsive';
import { checkUserAccess } from '../../../store/actions/user/checkAccess';

function External() {
  const isMobileOrTablet = useResponsive('down', 'md');
  const responseRef = useRef(null);

  // Using Lambda function instead of direct OpenAI client

  const [prompt, setPrompt] = useState('');
  const [query, setQuery] = useState('');
  const [questionAsked, setQuestionAsked] = useState(false);
  const [answerGenerated, setAnswerGenerated] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const [apiResponse, setApiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-scroll to response when streaming
  useEffect(() => {
    if (isStreaming && responseRef.current) {
      responseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [apiResponse, isStreaming]);

  const handleButtonClick = async () => {
    if (!prompt || prompt.trim().length < 1) {
      toast.error('Please ask a valid question');
      return;
    }

    setAnswerGenerated(true);
    setQuestionAsked(true);
    setQuery(prompt);
    setLoading(true);
    setIsStreaming(true);
    setApiResponse('');

    const access = await checkUserAccess();

    if (access && access.success === false) {
      toast.error(access.message);
      resetState();
      return;
    }

    try {
      // Call your AWS Lambda function
      const response = await fetch(
        'https://p9domvzgcd.execute-api.us-east-1.amazonaws.com/default/test-med-affairs-frontend',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to get response');
      }

      setLoading(false);
      setApiResponse(data.response);
      setIsStreaming(false);
      setAnswerGenerated(false);
      setPrompt('');
    } catch (e) {
      console.error('API Error:', e);
      toast.error('Something went wrong. Please try again.');
      resetState();
    }
  };

  const resetState = () => {
    setLoading(false);
    setIsStreaming(false);
    setAnswerGenerated(false);
    setQuestionAsked(false);
    setQuery('');
    setApiResponse('');
    setPrompt('');
  };

  const clearQuestions = () => {
    resetState();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !answerGenerated) {
      e.preventDefault();
      handleButtonClick();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {questionAsked ? (
        <Fade in timeout={500}>
          <Box>
            {/* Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1d5296 0%, #2563eb 100%)',
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/medaffairsAI/hero-flair.svg), 
                                   url(https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/medaffairsAI/little-grid.svg)`,
                  backgroundPosition: '50% 50%, 0 16px',
                  backgroundRepeat: 'no-repeat, repeat',
                  backgroundSize: '180%, 32px',
                  opacity: 0.1,
                },
              }}
            >
              {/* Beta Notice */}
              <Box
                sx={{
                  backgroundColor: 'rgba(27, 76, 137, 0.9)',
                  p: { xs: 2, md: 2.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <InfoIcon sx={{ color: '#60a5fa' }} />
                <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
                  The following is a trial version of medaffairs AI
                </Typography>
                {!isMobileOrTablet && (
                  <Chip label="BETA" size="small" sx={{ backgroundColor: 'white', color: 'black', fontWeight: 600 }} />
                )}
              </Box>

              {/* Main Content */}
              <Box sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label="External Search"
                    sx={{ backgroundColor: 'white', color: 'black', fontWeight: 600, mb: 2 }}
                  />
                  <Typography variant={isMobileOrTablet ? 'h4' : 'h3'} sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                    Ask me anything...
                  </Typography>
                </Box>

                {/* Input Section */}
                <Box sx={{ position: 'relative', maxWidth: '68rem', mx: 'auto' }}>
                  <Box
                    component="textarea"
                    placeholder="Ask me anything..."
                    disabled={answerGenerated}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{
                      width: '100%',
                      minHeight: '120px',
                      p: 2,
                      pb: 6,
                      border: '2px solid rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        borderColor: 'rgba(37, 99, 235, 0.5)',
                        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
                      },
                      '&:disabled': {
                        opacity: 0.7,
                        cursor: 'not-allowed',
                      },
                    }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 1,
                      left: 1,
                      right: 1,
                      p: 1,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '0 0 8px 8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="button"
                      onClick={clearQuestions}
                      disabled={answerGenerated}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        border: 'none',
                        backgroundColor: answerGenerated ? '#9ca3af' : '#6b7280',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: answerGenerated ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                          backgroundColor: '#4b5563',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </Box>

                    <Box
                      component="button"
                      onClick={handleButtonClick}
                      disabled={answerGenerated || !prompt.trim()}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        border: 'none',
                        backgroundColor: answerGenerated || !prompt.trim() ? '#9ca3af' : '#2563eb',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: answerGenerated || !prompt.trim() ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                          backgroundColor: '#1d4ed8',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Response Section */}
            <Grow in timeout={700}>
              <Box
                ref={responseRef}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden',
                }}
              >
                {/* Response Header */}
                <Box
                  sx={{
                    background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                    p: { xs: 2, md: 3 },
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <BubbleChartIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Search Result
                    </Typography>
                    {isStreaming && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Generating...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      p: 1.5,
                      borderRadius: 1,
                      fontStyle: 'italic',
                    }}
                  >
                    "{query}"
                  </Typography>
                </Box>

                {/* Response Content */}
                <Box sx={{ p: { xs: 3, md: 4 } }}>
                  {loading && !apiResponse ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body1" color="text.secondary">
                        Analyzing your question...
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        '& .markdown-content': {
                          lineHeight: 1.7,
                          fontSize: '16px',
                          '& h1': {
                            color: '#1f2937',
                            fontWeight: 700,
                            fontSize: '1.875rem',
                            mt: 3,
                            mb: 2,
                            borderBottom: '2px solid #e5e7eb',
                            pb: 1,
                          },
                          '& h2': {
                            color: '#1f2937',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            mt: 2.5,
                            mb: 1.5,
                          },
                          '& h3': {
                            color: '#1f2937',
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            mt: 2,
                            mb: 1,
                          },
                          '& p': {
                            mb: 1.5,
                            color: '#374151',
                            lineHeight: 1.7,
                          },
                          '& ul': {
                            pl: 0,
                            mb: 1.5,
                            listStyle: 'none',
                            '& li': {
                              position: 'relative',
                              mb: 0.75,
                              pl: 3,
                              color: '#374151',
                              lineHeight: 1.6,
                              '&::before': {
                                content: '"•"',
                                position: 'absolute',
                                left: 0,
                                color: '#2563eb',
                                fontWeight: 'bold',
                                fontSize: '1.2em',
                              },
                              // Nested ul styling
                              '& ul': {
                                mt: 0.5,
                                mb: 0.5,
                                pl: 0,
                                '& li': {
                                  pl: 3,
                                  '&::before': {
                                    content: '"◦"',
                                    color: '#059669',
                                    fontSize: '1.1em',
                                  },
                                  // Third level nesting
                                  '& ul': {
                                    '& li': {
                                      pl: 3,
                                      '&::before': {
                                        content: '"▪"',
                                        color: '#7c3aed',
                                        fontSize: '1em',
                                      },
                                      // Fourth level nesting
                                      '& ul': {
                                        '& li': {
                                          pl: 3,
                                          '&::before': {
                                            content: '"‣"',
                                            color: '#dc2626',
                                            fontSize: '1em',
                                          },
                                        },
                                      },
                                    },
                                  },
                                },
                              },
                            },
                          },
                          '& ol': {
                            pl: 0,
                            mb: 1.5,
                            counterReset: 'list-counter',
                            listStyle: 'none',
                            '& li': {
                              position: 'relative',
                              mb: 0.75,
                              pl: 3,
                              color: '#374151',
                              lineHeight: 1.6,
                              counterIncrement: 'list-counter',
                              '&::before': {
                                content: 'counter(list-counter) "."',
                                position: 'absolute',
                                left: 0,
                                color: '#2563eb',
                                fontWeight: 'bold',
                              },
                              // Nested ol styling
                              '& ol': {
                                mt: 0.5,
                                mb: 0.5,
                                pl: 0,
                                counterReset: 'nested-counter',
                                '& li': {
                                  pl: 3,
                                  counterIncrement: 'nested-counter',
                                  '&::before': {
                                    content: 'counter(list-counter) "." counter(nested-counter) "."',
                                    color: '#059669',
                                  },
                                  // Third level nesting
                                  '& ol': {
                                    counterReset: 'deep-counter',
                                    '& li': {
                                      pl: 3,
                                      counterIncrement: 'deep-counter',
                                      '&::before': {
                                        content:
                                          'counter(list-counter) "." counter(nested-counter) "." counter(deep-counter) "."',
                                        color: '#7c3aed',
                                      },
                                    },
                                  },
                                },
                              },
                              // Mixed nested lists (ol in ul, ul in ol)
                              '& ul': {
                                mt: 0.5,
                                mb: 0.5,
                                pl: 0,
                                '& li': {
                                  pl: 3,
                                  '&::before': {
                                    content: '"◦"',
                                    color: '#059669',
                                    fontSize: '1.1em',
                                  },
                                },
                              },
                            },
                          },
                          '& strong, & b': {
                            fontWeight: 700,
                            color: '#1f2937',
                          },
                          '& em, & i': {
                            fontStyle: 'italic',
                            color: '#4b5563',
                          },
                          '& code': {
                            backgroundColor: '#f3f4f6',
                            color: '#dc2626',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875em',
                            fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          },
                          '& pre': {
                            backgroundColor: '#1f2937',
                            color: '#f9fafb',
                            p: 2,
                            borderRadius: 2,
                            overflow: 'auto',
                            mb: 1.5,
                            '& code': {
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              p: 0,
                            },
                          },
                          '& blockquote': {
                            borderLeft: '4px solid #2563eb',
                            pl: 2,
                            py: 1,
                            backgroundColor: '#f8fafc',
                            fontStyle: 'italic',
                            color: '#4b5563',
                            mb: 1.5,
                          },
                          '& hr': {
                            border: 'none',
                            borderTop: '1px solid #e5e7eb',
                            my: 3,
                          },
                          '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            mb: 1.5,
                            '& th, & td': {
                              border: '1px solid #e5e7eb',
                              p: 1,
                              textAlign: 'left',
                            },
                            '& th': {
                              backgroundColor: '#f9fafb',
                              fontWeight: 600,
                            },
                          },
                        },
                      }}
                    >
                      <div className="markdown-content">
                        <ReactMarkdown
                          components={{
                            // Custom components for better rendering
                            p: ({ children }) => (
                              <Typography component="p" sx={{ mb: 1.5, color: '#374151', lineHeight: 1.7 }}>
                                {children}
                              </Typography>
                            ),
                            h1: ({ children }) => (
                              <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                  color: '#1f2937',
                                  fontWeight: 700,
                                  mt: 3,
                                  mb: 2,
                                  borderBottom: '2px solid #e5e7eb',
                                  pb: 1,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h2: ({ children }) => (
                              <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                  color: '#1f2937',
                                  fontWeight: 600,
                                  mt: 2.5,
                                  mb: 1.5,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h3: ({ children }) => (
                              <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                  color: '#1f2937',
                                  fontWeight: 600,
                                  mt: 2,
                                  mb: 1,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            ul: ({ children, depth = 0 }) => (
                              <Box
                                component="ul"
                                sx={{
                                  pl: 0,
                                  mb: depth === 0 ? 1.5 : 0.5,
                                  mt: depth === 0 ? 0 : 0.5,
                                  listStyle: 'none',
                                  '& > li': {
                                    position: 'relative',
                                    mb: 0.75,
                                    pl: 3,
                                    color: '#374151',
                                    lineHeight: 1.6,
                                    '&::before': {
                                      content: depth === 0 ? '"•"' : depth === 1 ? '"◦"' : depth === 2 ? '"▪"' : '"‣"',
                                      position: 'absolute',
                                      left: 0,
                                      color:
                                        depth === 0
                                          ? '#2563eb'
                                          : depth === 1
                                          ? '#059669'
                                          : depth === 2
                                          ? '#7c3aed'
                                          : '#dc2626',
                                      fontWeight: 'bold',
                                      fontSize: depth === 0 ? '1.2em' : depth === 1 ? '1.1em' : '1em',
                                    },
                                  },
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            ol: ({ children, depth = 0 }) => (
                              <Box
                                component="ol"
                                sx={{
                                  pl: 0,
                                  mb: depth === 0 ? 1.5 : 0.5,
                                  mt: depth === 0 ? 0 : 0.5,
                                  counterReset:
                                    depth === 0 ? 'list-counter' : depth === 1 ? 'nested-counter' : 'deep-counter',
                                  listStyle: 'none',
                                  '& > li': {
                                    position: 'relative',
                                    mb: 0.75,
                                    pl: 3,
                                    color: '#374151',
                                    lineHeight: 1.6,
                                    counterIncrement:
                                      depth === 0 ? 'list-counter' : depth === 1 ? 'nested-counter' : 'deep-counter',
                                    '&::before': {
                                      content:
                                        depth === 0
                                          ? 'counter(list-counter) "."'
                                          : depth === 1
                                          ? 'counter(list-counter) "." counter(nested-counter) "."'
                                          : 'counter(list-counter) "." counter(nested-counter) "." counter(deep-counter) "."',
                                      position: 'absolute',
                                      left: 0,
                                      color: depth === 0 ? '#2563eb' : depth === 1 ? '#059669' : '#7c3aed',
                                      fontWeight: 'bold',
                                    },
                                  },
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            li: ({ children }) => <Box component="li">{children}</Box>,
                            strong: ({ children }) => (
                              <Box component="strong" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                {children}
                              </Box>
                            ),
                            code: ({ children, inline }) =>
                              inline ? (
                                <Box
                                  component="code"
                                  sx={{
                                    backgroundColor: '#f3f4f6',
                                    color: '#dc2626',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.875em',
                                    fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                  }}
                                >
                                  {children}
                                </Box>
                              ) : (
                                <Box
                                  component="pre"
                                  sx={{
                                    backgroundColor: '#1f2937',
                                    color: '#f9fafb',
                                    p: 2,
                                    borderRadius: 2,
                                    overflow: 'auto',
                                    mb: 1.5,
                                  }}
                                >
                                  <code>{children}</code>
                                </Box>
                              ),
                          }}
                        >
                          {apiResponse}
                        </ReactMarkdown>
                      </div>
                      {isStreaming && (
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 2,
                            height: 20,
                            backgroundColor: '#2563eb',
                            animation: 'blink 1s infinite',
                            '@keyframes blink': {
                              '0%, 50%': { opacity: 1 },
                              '51%, 100%': { opacity: 0 },
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {!answerGenerated && !isStreaming && apiResponse && (
                    <Box
                      sx={{
                        mt: 3,
                        pt: 3,
                        borderTop: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                      }}
                    >
                      <Chip label="External Search" variant="filled" sx={{ fontWeight: 600 }} />
                      <Typography variant="caption" color="text.secondary">
                        Consider checking important information.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grow>
          </Box>
        </Fade>
      ) : (
        <Fade in timeout={500}>
          <Box>
            {/* Welcome Screen */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1d5296 0%, #2563eb 100%)',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                minHeight: { xs: '600px', md: '580px' },
                display: 'flex',
                flexDirection: 'column',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/medaffairsAI/hero-flair.svg), 
                                   url(https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/medaffairsAI/little-grid.svg)`,
                  backgroundPosition: '50% 50%, 0 16px',
                  backgroundRepeat: 'no-repeat, repeat',
                  backgroundSize: '180%, 32px',
                  opacity: 0.1,
                },
              }}
            >
              {/* Beta Notice */}
              <Box
                sx={{
                  backgroundColor: 'rgba(27, 76, 137, 0.9)',
                  p: { xs: 2, md: 2.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <InfoIcon sx={{ color: '#60a5fa' }} />
                <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
                  The following is a trial version of medaffairs AI
                </Typography>
                {!isMobileOrTablet && (
                  <Chip label="BETA" size="small" sx={{ backgroundColor: 'white', color: 'black', fontWeight: 600 }} />
                )}
              </Box>

              {/* Main Content */}
              <Box sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1, flex: 1 }}>
                <Box sx={{ mb: 4 }}>
                  <Chip
                    label="External Search"
                    sx={{ backgroundColor: 'white', color: 'black', fontWeight: 600, mb: 2 }}
                  />
                  <Typography variant={isMobileOrTablet ? 'h4' : 'h3'} sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                    Ask me anything...
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                    {isMobileOrTablet
                      ? 'For the best answers please keep your questions within the medical affairs space'
                      : 'Stay up to date and get real time medical affairs insights when you need it. For the best answers please keep your questions within the medical affairs space'}
                  </Typography>
                </Box>

                {/* Example Questions */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ backgroundColor: 'white' }}>
                      <BubbleChartIcon sx={{ color: '#2563eb' }} />
                    </Avatar>
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                      You can ask...
                    </Typography>
                  </Box>

                  <Box sx={{ pl: { xs: 0, md: 7 } }}>
                    {[
                      'What is the primary objective of Phase I, Phase II, and Phase III clinical trials?',
                      'What system is used in the European Union for reporting adverse drug reactions (ADRs)?',
                      'What document must be submitted to the FDA to initiate a clinical trial in the United States?',
                    ].map((question, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          p: 2,
                          borderRadius: 2,
                          mb: 1.5,
                          border: '1px solid rgba(255,255,255,0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                        onClick={() => setPrompt(question)}
                      >
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                          {question}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Input Section */}
              <Box sx={{ p: { xs: 3, md: 4 }, pt: 0, position: 'relative', zIndex: 1 }}>
                <Box sx={{ position: 'relative', maxWidth: '68rem', mx: 'auto' }}>
                  <Box
                    component="textarea"
                    placeholder="Ask me anything..."
                    disabled={answerGenerated}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{
                      width: '100%',
                      minHeight: '120px',
                      p: 2,
                      pb: 6,
                      border: '2px solid rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:focus': {
                        borderColor: 'rgba(37, 99, 235, 0.5)',
                        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
                      },
                      '&:disabled': {
                        opacity: 0.7,
                        cursor: 'not-allowed',
                      },
                    }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 1,
                      right: 1,
                      p: 1,
                    }}
                  >
                    <Box
                      component="button"
                      onClick={handleButtonClick}
                      disabled={answerGenerated || !prompt.trim()}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        border: 'none',
                        backgroundColor: answerGenerated || !prompt.trim() ? '#9ca3af' : '#2563eb',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: answerGenerated || !prompt.trim() ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                          backgroundColor: '#1d4ed8',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}
    </Container>
  );
}

export default withAuth(External);
